import React, { useState, useEffect, Suspense, useMemo } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  Navigate,
} from 'react-router';
import {
  useLocation,
  useNavigate,
  Route,
  Routes,
} from 'react-router-dom';
import { ACCESS_GATE } from '@lba-dev/package.local-globals/accessGate';

import Page404 from './Page404';
import DialogManager from './DialogManager';
import TokenManager from './TokenManager';
import { logout } from '../actions/auth';
import LoginId from '../components/Dashboard/LoginId';
import Notifications from '../components/Notifications';
import ActionNotification from '../components/Notifications/ActionNotification';
import Header from './Header.js';
import customTheme from '../config/customTheme';
import routers from '../config/router';
import LoadingManager from './LoadingManager';
import withMediaQuery from '../components/Hoc/WithMediaQuery';
import InstallGate from '../components/InstallGate';
import AccessGate from '../components/AccessGate';
import store from '../store.js';
import { setLoading } from '../actions/loading.js';


const defaultTheme = createTheme();
const theme = createTheme(customTheme(defaultTheme));

export const Dashboard = React.memo(({ hideLoader, ...props }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector(({
    auth
  }) => auth);

  const [loggedOnce, setLoggedOnce] = useState(auth.token !== undefined);

  const scrollToRef = () => {
    if (!!navigator.userAgent &&
      (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i)
        .test(navigator.userAgent)) {
      setTimeout(() =>
        window.scrollTo(0, 45), 1000);
    }
  };

  // ***** Webpack hot reload handling *****
  useEffect(() => {
    if (module.hot) {
      module.hot.addStatusHandler((status) => {
        if (status === 'check') {
          store.dispatch(setLoading({ message: 'Chargement en cours...',
            open: true }));
        } else if (status === 'idle') {
          store.dispatch(setLoading({ open: false }));
        }
      });
    }
  }, []);

  const authKeys = useMemo(() => Object.keys(auth), [auth]);

  useEffect(() => {
    scrollToRef();
    hideLoader();
    if (loggedOnce === false
      && auth.token
      && auth.token !== undefined
    ) {
      setLoggedOnce(true);
    }
    if (auth.token && authKeys.length === 0) {
      setLoggedOnce(false);
    }
  }, [hideLoader, loggedOnce, auth.token, authKeys]);

  const deconnexion = () => {
    navigate('/login', {
      state: {
        from: location
      }
    });
    dispatch(logout());
  };

  const url = location?.state?.redirectPage;
  const autoriseRoute = auth.artisan && loggedOnce
    ? routers(auth.artisan)
    : [];

  // Accès bloqué → on ne monte aucune route métier : seule la route de
  // résolution existe, tout le reste rend le AccessGate (rien en
  // arrière-plan à révéler). La règle vit dans lib (ACCESS_GATE),
  // consommée ici et par le middleware backend — source unique.
  const accessBlocked = !!auth.artisan && ACCESS_GATE.isBlocked(auth.artisan);

  const routesToRender = accessBlocked
    ? autoriseRoute.filter((e) => e.path === ACCESS_GATE.resolvePath)
    : autoriseRoute;

  return (
    <ThemeProvider theme={theme}>
      <div style={{ height: '100%', paddingTop: '56px' }}>
        <Header
          user={auth.artisan}
          deconnexion={deconnexion}
          navigate={navigate}
          pageName={location.pathname}
        />
        <TokenManager
          location={location}
          navigate={navigate}
          auth={auth}
        />
        {loggedOnce && auth.artisan && <InstallGate />}
        <Routes>
          {!loggedOnce && url
            ? <Route path={`${url}` || '/'} element={
              <Navigate to={`${url}` || '/'}
                {...(location.state.redirectPage
                  ? { state: location.state }
                  : {})}
              />
            } />
            : <Route path={'/login'} element={<LoginId />} />
          }
          {loggedOnce ? (
            routesToRender.map((e) => (
              <Route
                exact
                key={e.path}
                {...e}
                element={
                  <Suspense fallback={<></>}>
                    <e.component
                      enabled={autoriseRoute}
                      {...props}
                      {...e.componentProps}
                    />
                  </Suspense>}
              />
            ))
          ) : (
            <Route path='*' element={
              <Navigate
                to={'/login'}
                state={{
                  from: location === '/login'
                    ? '/'
                    : location,
                }}
              />
            } />
          )}
          {loggedOnce && accessBlocked
            ? <Route path='*' element={<AccessGate />} />
            : <Route path='*' element={<Page404 />} />
          }
        </Routes>
        <Notifications />
        <ActionNotification />
        <DialogManager />
        <LoadingManager />
      </div>
    </ThemeProvider>
  );
});

export default withMediaQuery()(Dashboard);
